import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of } from 'rxjs';
import { AppInsightsService } from '../../../../core/services/app-insights/app-insights.service';
import { ProxyUserRequestDuplicate } from '../../models/proxy-user-request';
import { ProxyUserRequestsService } from '../../services/proxy-user-requests/proxy-user-requests.service';
import {
  submitProxyUserRequest,
  submitProxyUserRequestDuplicate,
  submitProxyUserRequestFailure,
  submitProxyUserRequestSuccess,
} from '../actions/proxy-access.actions';

@Injectable()
export class ProxyAccessEffects {
  private actions$ = inject(Actions);
  private proxyUserRequestsService = inject(ProxyUserRequestsService);
  private appInsightsService = inject(AppInsightsService);

  /**
   * `exhaustMap`, not `mergeMap`: this is a user-triggered write, and a double-click must not post
   * two authorizations. Further attempts are ignored until the in-flight one settles.
   *
   * Only the outcome is tracked — never the action's `request`, which carries the patient's name,
   * date of birth, MRN and SSN digits.
   */
  submitProxyUserRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submitProxyUserRequest),
      exhaustMap(action => {
        return this.proxyUserRequestsService.insertProxyUserRequest(action.request).pipe(
          map(response => {
            this.appInsightsService.trackEvent('ProxyAccess.Submit.Success');
            return submitProxyUserRequestSuccess({ response });
          }),
          catchError((err: HttpErrorResponse) => {
            // AC6: a duplicate is a settled outcome, not something to retry — the caregiver already
            // has a request in the queue and resubmitting would only add a second one. Split out
            // before the generic failure so the form can quote the existing reference number.
            const duplicate = this.toDuplicate(err);
            if (duplicate) {
              this.appInsightsService.trackEvent('ProxyAccess.Submit.Duplicate');
              return of(submitProxyUserRequestDuplicate({ duplicate }));
            }

            this.appInsightsService.trackEvent('ProxyAccess.Submit.Failure');
            return of(submitProxyUserRequestFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  /**
   * Reads the request already on file out of a 409 Conflict body.
   *
   * Both halves of the check matter. A 409 is the API's duplicate signal, but only a body carrying
   * the existing record's id lets the form tell the user *which* request is pending — without it
   * there is nothing to show beyond "you already asked", so it falls through to the retryable error
   * rather than rendering a panel with an empty reference number. `status` and `createdOnUtc` are
   * optional for the same reason: absent, the panel simply says less.
   *
   * Nothing from the body is logged — a proxy-access record is tied to a named patient.
   */
  private toDuplicate(err: HttpErrorResponse): ProxyUserRequestDuplicate | null {
    if (err.status !== HttpStatusCode.Conflict) return null;

    const body = err.error as Partial<ProxyUserRequestDuplicate> | null | undefined;
    if (typeof body?.patientPortalProxyUserRequestId !== 'number') return null;

    return {
      patientPortalProxyUserRequestId: body.patientPortalProxyUserRequestId,
      status: body.status ?? null,
      createdOnUtc: body.createdOnUtc ?? null,
    };
  }
}
